import Color from 'color';
import { CONNECTOR_END_STYLE, STROKE_TYPES } from '../constants';
import { defaultColorPalette } from '../utils/DefaultsManager';
import { isMindMap } from './guards';
import type MindMap from './MindMap';
import { type MindMapTheme } from './MindMap';
import type MindMapBranch from './MindMapBranch';
import type MindMapLeaf from './MindMapLeaf';

export const DEFAULT_LEAF_WIDTH = 200;
export const DEFAULT_LEAF_HEIGHT = 52;
export const MAX_TEXT_LUM = 50;
export const WRAP_LEAF_TEXT = true;

/// Style fields child leaves will copy from their parent
export const CUSTOM_THEME_FIELDS = [
  'stroke',
  'strokeWidth',
  'strokeDouble',
  'strokeType',
  'fill',
  'fontSize',
  'textAlign',
  'textColor',
];

export const COLOR_SEQUENCE = [
  defaultColorPalette[13],
  defaultColorPalette[14],
  '#CCA8FF', // Missing color - Amethyst/200
  defaultColorPalette[15],
  defaultColorPalette[16],
  defaultColorPalette[12],
  defaultColorPalette[11],
  defaultColorPalette[6],
  defaultColorPalette[17],
  defaultColorPalette[8],
  defaultColorPalette[5],
  defaultColorPalette[9],
  defaultColorPalette[7],
  defaultColorPalette[2],
];

/// Minimum brightness for font colour so it remains readable
function safeColor(color: Color | string): string {
  let safe = Color(color).hsl();
  if (safe.l() > MAX_TEXT_LUM) {
    safe = safe.l(MAX_TEXT_LUM);
  }
  return safe.hex();
}

const BASE_THEME = {
  label: '',
  root() {
    return {
      shapeName: 'processEnd',
      strokeWidth: 4,
      strokeType: STROKE_TYPES.SOLID,
      fontSize: 32,
      width: 2 * DEFAULT_LEAF_WIDTH,
      height: 109,
    };
  },
  leaf() {
    return {
      textAlign: 'center',
      fontSize: 14,
      strokeWidth: 3,
      wrapText: WRAP_LEAF_TEXT,
      shapeName: 'roundRect',
      strokeType: STROKE_TYPES.SOLID,
      fill: '#fff',
      textMargin: 16,
      width: DEFAULT_LEAF_WIDTH,
      height: DEFAULT_LEAF_HEIGHT,
    };
  },
  connector() {
    return {
      endStyles: [CONNECTOR_END_STYLE.NONE, CONNECTOR_END_STYLE.FILLED_ARROW],
      strokeType: STROKE_TYPES.SOLID,
      cornerSize: 16,
      strokeWidth: 2,
      stroke: '#000',
    };
  },
} satisfies MindMapTheme;

export const MindMapThemes = {
  branches: {
    label: 'Color by branch',
    root() {
      const stroke = COLOR_SEQUENCE[0];
      const textColor = safeColor(stroke);
      return {
        ...BASE_THEME.root(),
        textColor,
        stroke,
        fill: '#fff',
      };
    },
    leaf(branch: MindMapBranch, index: number) {
      let stroke = 'green';
      const root = branch.getRootObject()!;
      if (root?.stroke) {
        // Match stroke colour of bottom leaf
        stroke = root.stroke;
      } else {
        // Skip first colour, that's used by the root node
        const colorIndex = 1 + index;
        stroke = COLOR_SEQUENCE[colorIndex % COLOR_SEQUENCE.length];
      }

      const textColor = safeColor(stroke);
      return {
        ...BASE_THEME.leaf(),
        stroke,
        textColor: textColor,
      };
    },
    connector: (parent: MindMapLeaf | MindMap, child: MindMapLeaf) => {
      let stroke = 'red';
      if (isMindMap(parent)) {
        // Root connectors match the root shape stroke
        stroke = parent.stroke || '#000';
      } else {
        const root = child.getBranch()!.getRootObject()!;
        if (root?.stroke) {
          // Match stroke colour of bottom leaf
          stroke = root.stroke;
        }
      }
      return {
        ...BASE_THEME.connector(),
        stroke,
      };
    },
  },
  rainbow: {
    label: 'Color by level',
    root() {
      const stroke = COLOR_SEQUENCE[0];
      const textColor = safeColor(stroke);
      return {
        ...BASE_THEME.root(),
        shapeName: 'processEnd',
        fontSize: 32,
        fill: '#fff',
        textColor,
        strokeType: STROKE_TYPES.SOLID,
        stroke,
      };
    },
    leaf(branch) {
      const depth = branch.depth();
      const stroke = COLOR_SEQUENCE[depth % COLOR_SEQUENCE.length];
      const textColor = safeColor(stroke);
      return {
        ...BASE_THEME.leaf(),
        shapeName: 'roundRect',
        textColor,
        stroke,
      };
    },
    connector(parent) {
      return {
        ...BASE_THEME.connector(),
        strokeType: STROKE_TYPES.SOLID,
        stroke: parent.stroke ?? parent.fill,
      };
    },
  },
  custom: {
    label: 'No theme',
    root(mindmap: MindMap) {
      const stroke = mindmap?.stroke || '#000';
      const textColor = safeColor(stroke);
      return {
        ...BASE_THEME.root(),
        shapeName: 'processEnd',
        fontSize: 32,
        fill: '#fff',
        textColor,
        stroke,
      };
    },
    leaf(branch: MindMapBranch) {
      const styles = {
        ...BASE_THEME.leaf(),
      };

      const mindmap = branch.getMindMap();
      if (!mindmap) return styles;

      for (const key of CUSTOM_THEME_FIELDS) {
        // Ignore fontsize from root
        if (key === 'fontSize') {
          continue;
        }
        styles[key] = mindmap[key];
      }
      return styles;
    },
    connector(parent: MindMapLeaf | MindMap) {
      const stroke = parent.stroke || '#000';
      return {
        ...BASE_THEME.connector(),
        stroke,
      };
    },
  },
} satisfies Record<string, MindMapTheme & Record<string, unknown>>;

export type MindMapThemeNames = keyof typeof MindMapThemes;

export default MindMapThemes;
